import { Card, CardContent, CardDescription, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Pencil,
  Trash2,
  Upload,
  Image as ImageIcon,
  Loader2,
  ChevronDown,
  ChevronUp,
  GitBranch,
  BookOpen
} from "lucide-react";

export const StepCard = ({
  step,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  onQuickRefUpload,
  uploadingRef,
  getPhaseColor,
  getPhaseLabel,
  productInputFields = [],
}) => {
  return (
    <Card
      className={`card-hover ${expanded ? 'ring-2 ring-primary' : ''}`}
      data-testid={`step-card-${step.step_number}`}
    >
      <div
        className="cursor-pointer p-6"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center font-mono font-bold">
              {step.step_number}
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                {step.name}
                <Badge
                  style={{ backgroundColor: getPhaseColor(step.phase, step.phase_id) }}
                  className="text-white max-w-[160px] truncate shrink-0"
                  title={getPhaseLabel(step.phase, step.phase_id, step.phase_name)}
                >
                  {getPhaseLabel(step.phase, step.phase_id, step.phase_name)}
                </Badge>
                {(() => {
                  const conditions = step.step_condition?.conditions
                    || (step.step_condition?.when_field ? [{ when_field: step.step_condition.when_field, when_values: [step.step_condition.when_value] }] : []);
                  const summary = conditions
                    .filter(c => c.when_field && (c.when_values || []).filter(Boolean).length > 0)
                    .map(c => {
                      const field = productInputFields.find(f => f.key === c.when_field);
                      const label = field ? field.label : `${c.when_field} (deleted)`;
                      return `${label} = ${(c.when_values || []).filter(Boolean).join("/")}`;
                    })
                    .join(" AND ");
                  return summary ? (
                    <Badge variant="outline" className="gap-1 text-orange-600 border-orange-400 bg-orange-50">
                      <GitBranch className="w-3 h-3" />
                      {summary}
                    </Badge>
                  ) : null;
                })()}
                {step.is_optional && (
                  <Badge variant="outline" className="gap-1 text-blue-600 border-blue-400 bg-blue-50">
                    <BookOpen className="w-3 h-3" />
                    Optional
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="mt-1">{step.cv_task}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => { e.stopPropagation(); onEdit(step); }}
              data-testid={`edit-step-${step.step_number}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={(e) => { e.stopPropagation(); onDelete(step.id); }}
              data-testid={`delete-step-${step.step_number}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <CardContent className="pt-0">
          <div className="grid gap-4 md:grid-cols-2 border-t pt-4">
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Description</p>
                <p className="text-sm">{step.description || "No description"}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pass Criteria</p>
                <p className="text-sm">{step.pass_criteria || "No criteria defined"}</p>
              </div>
              {step.expected_values && Object.keys(step.expected_values).length > 0 && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Expected Values</p>
                  <pre className="text-xs font-mono bg-muted p-2 rounded mt-1">
                    {JSON.stringify(step.expected_values, null, 2)}
                  </pre>
                </div>
              )}
              {(() => {
                const conditions = step.step_condition?.conditions
                  || (step.step_condition?.when_field ? [{ when_field: step.step_condition.when_field, when_values: [step.step_condition.when_value] }] : []);
                const activeConditions = conditions.filter(c => c.when_field && (c.when_values || []).filter(Boolean).length > 0);
                if (activeConditions.length === 0) return null;
                return (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                      <GitBranch className="w-3.5 h-3.5 text-orange-500" />
                      Step Condition
                    </p>
                    <div className="mt-1 space-y-1">
                      {activeConditions.map((c, idx) => {
                        const field = productInputFields.find(f => f.key === c.when_field);
                        return (
                        <div key={idx} className="flex items-center gap-2 text-sm flex-wrap">
                          <span className={field ? "font-medium" : "font-medium text-destructive"}>
                            {field ? field.label : `${c.when_field} (field no longer exists)`}
                          </span>
                          <span className="text-muted-foreground">=</span>
                          {(c.when_values || []).filter(Boolean).map((v) => (
                            <Badge key={v} variant="secondary" className="font-mono text-xs">
                              {v}
                            </Badge>
                          ))}
                        </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      This step is only included when every condition above is met at test creation.
                    </p>
                  </div>
                );
              })()}
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-muted-foreground">Reference Image</p>
                <div>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    id={`quick-upload-${step.id}`}
                    onChange={(e) => onQuickRefUpload(step.id, e)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById(`quick-upload-${step.id}`).click();
                    }}
                    disabled={uploadingRef === step.id}
                    data-testid={`upload-ref-${step.step_number}`}
                  >
                    {uploadingRef === step.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </>
                    )}
                  </Button>
                </div>
              </div>
              {(step.reference_image_url || step.reference_image_base64) ? (
                <img
                  src={step.reference_image_url || `data:image/jpeg;base64,${step.reference_image_base64}`}
                  alt="Reference"
                  className="w-full rounded-lg border"
                />
              ) : (
                <div className="aspect-video bg-muted rounded-lg border-2 border-dashed flex items-center justify-center">
                  <div className="text-center text-muted-foreground">
                    <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No reference image</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};
